import { useEffect, useState, type ReactNode } from 'react'
import { get } from '../lib/api'
import type { User } from '../lib/auth'
import { t, useLocale } from '../lib/i18n'
import { canGoBack, Link, navigate, useRoute, type Route, type RouteName } from '../lib/route'
import { featureFor, useFeatures, type FeatureKey } from '../lib/features'
import type { Health } from '../App'
import Icon, { type IconName } from './Icon'
import Admin from './Admin'
import SignIn from './SignIn'
import LanguageToggle from './LanguageToggle'
import EnvBanner from './EnvBanner'
import UpdateBar from './UpdateBar'
import InstallPrompt from './InstallPrompt'
import DebugPanel from './DebugPanel'
import Feed from '../screens/Feed'
import OpportunityDetail from '../screens/OpportunityDetail'
import InspectionMode from '../screens/InspectionMode'
import Board from '../screens/Board'
import Compare from '../screens/Compare'
import MapScreen from '../screens/MapScreen'
import Search from '../screens/Search'
import Intake from '../screens/Intake'
import Setup from '../screens/setup/Setup'
import Settings, { FeatureOff } from '../screens/Settings'

/**
 * The frame.
 *
 * ── What changed, and why ────────────────────────────────────────────────────
 *
 * This used to be a five-icon tab bar and a 48px header, which is a phone
 * design and only a phone design. On anything wider it left the application
 * with no navigation worth the name, and by the time there were nine screens
 * and ten stacked panels on a listing nobody could find anything. So the chrome
 * is now the one lifted from `website/`: an identity strip, a masthead with the
 * wordmark, horizontal navigation and one primary action, and a bounded content
 * column with a real page header. The bottom bar still exists — below 800px,
 * where the masthead nav is hidden and a thumb needs somewhere to reach.
 *
 * ── Everything is a switch ───────────────────────────────────────────────────
 *
 * `lib/features.ts` decides what appears. Every entry is ON until the owner
 * switches it off, so the navigation below is built from what is currently lit
 * rather than from a fixed list. Two things are never switchable: the hunt,
 * which is the application, and settings, which is how you get the rest back.
 *
 * A route whose switch is off renders `FeatureOff` and not a 404. A push
 * notification or a bookmark can land on a screen somebody has switched off,
 * and "not found" there is a lie that reads as a crash.
 *
 * ── Why the route switch lives here ──────────────────────────────────────────
 *
 * `public/sw.js` calls `client.navigate(target)` when a push is tapped, and
 * `notify.ts` writes targets like `/o/op_abc`. That only works if a path maps
 * to a screen, so the mapping is one place and it is this one.
 */

export default function Shell({ user, health, onAuthChange }: {
  user: User | null
  health: Health | null
  onAuthChange: () => Promise<void>
}) {
  /*
   * The whole reason `useLocale` exists. Subscribed once, at the root, so every
   * `t(key)` below and in every child re-evaluates the moment the language
   * changes — with no reload, which is what made the toggle unusable before.
   */
  useLocale()
  const route = useRoute()
  const on = useFeatures()
  const signedIn = !!user
  const onInbox = route.name === 'inbox'
  const [unread, setUnread] = useState(0)

  const wantsInbox = signedIn && on('nav.inbox')

  useEffect(() => {
    if (!wantsInbox) return
    let live = true

    const load = async () => {
      try {
        const body = await get<{ notifications: { read_at: number | null }[] }>('/api/notifications')
        if (!live) return
        /*
         * Sitting on the inbox is what reads the rows, so while it is open the
         * badge is simply zero rather than a number racing the screen that
         * clears it. Leaving re-runs this effect and asks the server again.
         */
        setUnread(onInbox ? 0 : body.notifications.filter((n) => n.read_at === null).length)
      } catch {
        /* Offline. Keep the last count — a false zero says "nothing waiting". */
      }
    }

    void load()
    window.addEventListener('focus', load)
    return () => {
      live = false
      window.removeEventListener('focus', load)
    }
  }, [wantsInbox, onInbox])

  // Signed out is a different composition, not a disabled version of this one:
  // there is no navigation to show and nothing it could usefully lead to.
  if (!user) {
    return (
      <>
        <UpdateBar />
        <EnvBanner health={health} />
        <main className="signin-wrap">
          <SignIn onSignedIn={onAuthChange} />
          <div className="signin-lang"><LanguageToggle /></div>
        </main>
        <Footer health={health} />
      </>
    )
  }

  const nav = NAV.filter((item) => !item.feature || on(item.feature))
  const here = NAV_OF[route.name]
  const meta = pageMeta(route)
  const initials = initialsOf(user)

  return (
    <>
      {/* Above everything: an app running old code is the cause of bug reports
          nobody can reproduce. */}
      <UpdateBar />
      <EnvBanner health={health} />

      <a className="skip" href="#content">{t('shell.skipToContent')}</a>

      {/* The identity strip. Which application, which environment, which
          language — the three questions a screenshot cannot otherwise answer. */}
      <div className="strip">
        <span className="strip-what">
          <b>{health?.app ?? 'Homestead Hunter'}</b>
          <span>{health ? health.environment : ''}</span>
        </span>
        <LanguageToggle />
      </div>

      <header className="masthead">
        <Link to="/feed" className="brand">
          <span>Homestead <em>Hunter</em></span>
          <small>PR</small>
        </Link>

        <nav aria-label={t('nav.primary')}>
          {nav.map((item) => (
            <Link
              key={item.name}
              to={item.to}
              className={here === item.name ? 'is-on' : ''}
              aria-current={here === item.name ? 'page' : undefined}
            >
              <Icon name={item.icon} size={17} />
              {t(item.label)}
            </Link>
          ))}
        </nav>

        <div className="head-actions">
          {on('nav.search') && (
            <Link to="/search" className="icon-btn" aria-label={t('shell.search')}>
              <Icon name="search" size={20} />
            </Link>
          )}
          {on('nav.inbox') && (
            <Link to="/inbox" className="icon-btn" aria-label={t('shell.alerts')}>
              <Icon name="bell" size={20} />
              {unread > 0 && (
                <span className="badge" aria-label={`${unread} ${t('shell.unread')}`}>
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </Link>
          )}
          {on('nav.intake') && (
            <Link to="/intake" className="cta">
              <Icon name="plus" size={17} />
              <span>{t('nav.add')}</span>
            </Link>
          )}
          <Link
            to="/settings"
            className={`avatar ${here === 'settings' ? 'is-on' : ''}`}
            aria-label={t('route.settings')}
            aria-current={here === 'settings' ? 'page' : undefined}
          >
            {initials}
          </Link>
        </div>
      </header>

      <main id="content" className="shell">
        {meta && (
          <div className="page-head">
            <div className="page-head-text">
              {meta.back && (
                <button
                  type="button"
                  className="back-link"
                  /*
                   * Not `history.back()` unconditionally: arriving by tapping a
                   * push lands straight on `/o/op_abc` with a history full of
                   * entries that are not ours, and going back from there leaves
                   * the application — which reads as a crash.
                   */
                  onClick={() => (canGoBack() ? history.back() : navigate('/feed', { replace: true }))}
                >
                  <Icon name="back" size={15} />
                  {t('shell.back')}
                </button>
              )}
              <p className="eyebrow">
                <Icon name={meta.icon} size={13} />
                {t(meta.eyebrow)}
              </p>
              <h1>{t(meta.title)}</h1>
              {meta.lede && <p>{t(meta.lede)}</p>}
            </div>
          </div>
        )}

        <Screen route={route} user={user} health={health} on={on} onAuthChange={onAuthChange} />

        {/* Below the fold, not in the way. For an app that is not on a store. */}
        {on('tools.install') && <InstallPrompt />}
      </main>

      <Footer health={health} />

      {/* Phone only. Above 800px the masthead nav is the navigation and a
          second copy of it at the bottom is clutter. */}
      <nav className="mobile-nav" aria-label={t('nav.primary')}>
        {nav.slice(0, 4).map((item) => (
          <Link
            key={item.name}
            to={item.to}
            className={`${item.cta ? 'is-cta' : ''} ${here === item.name ? 'is-on' : ''}`}
            aria-current={here === item.name ? 'page' : undefined}
          >
            <span className="tab-glyph"><Icon name={item.icon} size={item.cta ? 22 : 21} /></span>
            <span className="tab-label">{t(item.label)}</span>
          </Link>
        ))}
        {on('nav.intake') && (
          <Link to="/intake" className={`is-cta ${here === 'intake' ? 'is-on' : ''}`}>
            <span className="tab-glyph"><Icon name="plus" size={22} /></span>
            <span className="tab-label">{t('nav.add')}</span>
          </Link>
        )}
        <Link
          to="/settings"
          className={here === 'settings' ? 'is-on' : ''}
          aria-current={here === 'settings' ? 'page' : undefined}
        >
          <span className="tab-glyph"><Icon name="settings" size={21} /></span>
          <span className="tab-label">{t('route.settings')}</span>
        </Link>
      </nav>

      {/* Capability AND a switch: the phone that needs diagnosing is always a
          real one in production, but the drawer sits over the bottom-right of
          every screen and has no business being there the rest of the time. */}
      {on('tools.debug') && user.capabilities.includes('errors.read') && (
        <DebugPanel environment={health?.environment ?? '—'} />
      )}
    </>
  )
}

/* ── The navigation ────────────────────────────────────────────────────────
   Hunt is not switchable — it is the application. Everything else appears only
   when its switch is on, which is why this is a filter and not a fixed row.
   A nav item is `on` for a whole FAMILY of routes: opening a listing from the
   hunt must not black out the whole bar, because "where am I" is the one
   question navigation exists to answer. */

type NavName = 'hunt' | 'board' | 'map' | 'compare' | 'inspect' | 'intake' | 'search' | 'inbox' | 'settings'

const NAV: {
  name: NavName
  to: string
  icon: IconName
  label: 'nav.hunt' | 'nav.board' | 'nav.map' | 'route.compare' | 'route.inspect' | 'nav.add'
  feature?: FeatureKey
  cta?: boolean
}[] = [
  { name: 'hunt', to: '/feed', icon: 'home', label: 'nav.hunt' },
  { name: 'board', to: '/board', icon: 'chart', label: 'nav.board', feature: 'nav.board' },
  { name: 'map', to: '/map', icon: 'pin', label: 'nav.map', feature: 'nav.map' },
  { name: 'compare', to: '/compare', icon: 'people', label: 'route.compare', feature: 'nav.compare' },
  { name: 'inspect', to: '/inspect', icon: 'camera', label: 'route.inspect', feature: 'nav.inspect' },
]

const NAV_OF: Partial<Record<RouteName, NavName>> = {
  feed: 'hunt',
  opportunity: 'hunt',
  search: 'hunt',
  board: 'board',
  map: 'map',
  compare: 'compare',
  inspect: 'inspect',
  intake: 'intake',
  inbox: 'inbox',
  settings: 'settings',
  me: 'settings',
  setup: 'settings',
  admin: 'settings',
}

/**
 * Which switch a route needs. `null` means the route is always reachable.
 *
 * This is the single table that decides whether a path renders its screen or
 * renders `FeatureOff`, and it is separate from `NAV` because a route can be
 * reachable by link without owning a place in the navigation.
 */
const NEEDS: Partial<Record<RouteName, FeatureKey>> = {
  board: 'nav.board',
  map: 'nav.map',
  compare: 'nav.compare',
  inspect: 'nav.inspect',
  intake: 'nav.intake',
  search: 'nav.search',
  inbox: 'nav.inbox',
  setup: 'tools.setup',
  admin: 'tools.admin',
}

/* ── The page header ─────────────────────────────────────────────────────── */

interface PageMeta {
  icon: IconName
  eyebrow: Parameters<typeof t>[0]
  title: Parameters<typeof t>[0]
  lede?: Parameters<typeof t>[0]
  back?: boolean
}

/**
 * The header for a route, or `null` for one that draws its own.
 *
 * The hunt draws nothing here: it opens on its own filters and its own contrast
 * strip, and a serif page title above them would push the first photograph off
 * a phone screen. A listing draws nothing either — the photograph is the title.
 */
function pageMeta(route: Route): PageMeta | null {
  switch (route.name) {
    case 'feed': return null
    case 'opportunity': return null
    case 'board':
      return { icon: 'chart', eyebrow: 'page.eyebrowPipeline', title: 'nav.board', lede: 'page.boardLede' }
    case 'map':
      return { icon: 'pin', eyebrow: 'page.eyebrowGround', title: 'nav.map', lede: 'page.mapLede' }
    case 'compare':
      return { icon: 'people', eyebrow: 'page.eyebrowDecide', title: 'route.compare', lede: 'page.compareLede', back: true }
    case 'inspect':
      return { icon: 'camera', eyebrow: 'page.eyebrowGround', title: 'route.inspect', lede: 'page.inspectLede', back: true }
    case 'intake':
      return { icon: 'plus', eyebrow: 'page.eyebrowCapture', title: 'route.intake', lede: 'page.intakeLede', back: true }
    case 'search':
      return { icon: 'search', eyebrow: 'page.eyebrowFind', title: 'shell.search', back: true }
    case 'inbox':
      return { icon: 'bell', eyebrow: 'page.eyebrowWatching', title: 'shell.alerts', lede: 'page.inboxLede' }
    case 'settings':
    case 'me':
      return { icon: 'settings', eyebrow: 'page.eyebrowYours', title: 'route.settings', lede: 'page.settingsLede' }
    case 'setup':
      return { icon: 'key', eyebrow: 'page.eyebrowYours', title: 'route.setup', back: true }
    case 'admin':
      return { icon: 'shield', eyebrow: 'page.eyebrowYours', title: 'admin.title', back: true }
    default:
      return null
  }
}

/* ── The route switch ─────────────────────────────────────────────────────── */

function Screen({ route, user, health, on, onAuthChange }: {
  route: Route
  user: User
  health: Health | null
  on: (key: FeatureKey) => boolean
  onAuthChange: () => Promise<void>
}) {
  /*
   * The switch check comes FIRST, before the capability check and before the
   * screen. A screen somebody has merely switched off must say so and offer the
   * switch — answering 404 there would make a working bookmark look broken.
   */
  const needs = NEEDS[route.name]
  if (needs && !on(needs)) return <FeatureOff feature={featureFor(needs)} />

  switch (route.name) {
    /*
     * Every screen below was built, tested and left UNREACHABLE — `Shell.tsx`
     * belonged to the frame unit, which finished before the screens existed,
     * and no unit owned it afterwards. That is the fourth time this repository
     * has shipped a complete feature nobody mounted: `tickets.ts`, `voice.ts`,
     * and both halves of this. The API has `route_inventory.test.ts` to make it
     * impossible there; `shell_mount.test.ts` is the client's equivalent.
     *
     * `inbox` is still pending because no unit built it. That is a gap, not an
     * oversight, and it reads as one on screen.
     */
    case 'feed': return <Feed />
    case 'opportunity': return <OpportunityDetail id={route.params.id} />
    case 'inspect': return <InspectionMode />
    case 'board': return <Board />
    case 'compare': return <Compare />
    case 'map': return <MapScreen />
    case 'search': return <Search />
    case 'intake': return <Intake />
    case 'inbox': return pending('/inbox')

    case 'setup':
      /*
       * Capability-driven, so the screen is absent rather than present and
       * denied — and the server refuses the writes regardless. A rule the UI
       * enforces is not enforced.
       */
      if (!user.capabilities.includes('settings.write')) return <NotFound />
      return <Setup section={route.params.section} />

    /* `/me` was the old path and still arrives from bookmarks and one push
       target. It is the same screen. */
    case 'me':
    case 'settings':
      return <Settings user={user} health={health} onAuthChange={onAuthChange} />

    case 'admin':
      if (!user.capabilities.includes('admin.access')) return <NotFound />
      return <Admin />

    default:
      return <NotFound />
  }
}

/**
 * A screen the screens unit has not landed yet. It names its own path, so the
 * frame is walkable and a wrong route is visible rather than blank — a blank
 * screen and an unbuilt screen look identical, and only one of them is a bug.
 */
function pending(path: string): ReactNode {
  return (
    <section className="card pending">
      <h2>{t('shell.pending')}</h2>
      <p className="muted">{t('shell.pendingBody')}</p>
      <code>{path}</code>
    </section>
  )
}

function NotFound() {
  return (
    <section className="card">
      <h2>{t('shell.notFound')}</h2>
      <p className="muted">{t('shell.notFoundBody')}</p>
      <Link to="/feed" className="me-link"><Icon name="home" size={20} />{t('shell.goHunt')}</Link>
    </section>
  )
}

/**
 * Two letters for the avatar. The display name first, because that is what the
 * person calls themselves; the handle as the fallback, which is never empty.
 */
function initialsOf(user: User): string {
  const source = (user.displayName || user.handle || '?').trim()
  const words = source.split(/\s+/).filter(Boolean)
  const first = words[0] ?? ''
  const second = words[1] ?? ''
  if (first && second) return (first.slice(0, 1) + second.slice(0, 1)).toUpperCase()
  return source.slice(0, 2).toUpperCase()
}

function Footer({ health }: { health: Health | null }) {
  return (
    <footer className="app-footer">
      <span>{health ? `${health.app} · ${health.version}` : ''}</span>
      <span>{health ? `${health.commit} · ${health.environment}` : ''}</span>
    </footer>
  )
}
